import React, { useEffect, useState } from "react";
import { Trophy, Target, TrendingUp, ChevronRight } from "lucide-react";
import api from "../api/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

function useLang(): "ar" | "en" {
  if (typeof document !== "undefined") {
    const dir = document.documentElement.getAttribute("dir");
    if (dir === "rtl") return "ar";
  }
  const stored = (typeof localStorage !== "undefined" && localStorage.getItem("lang")) || "";
  if (stored === "ar") return "ar";
  return "en";
}

function formatPeriod(p: string, lang: "ar" | "en"): string {
  if (!p) return "";
  const [y, m] = p.split("-").map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { month: "long", year: "numeric" });
}

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function lastMonths(count: number): string[] {
  const arr: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return arr;
}

function scoreColor(score: number): string {
  if (score >= 90) return "text-yellow-600";   // Exceptional
  if (score >= 80) return "text-green-600";    // Excellent
  if (score >= 70) return "text-blue-600";     // Good
  if (score >= 60) return "text-gray-600";     // Satisfactory
  if (score >= 50) return "text-orange-600";   // Needs improvement
  return "text-red-500";                       // Underperforming
}

function scoreBarColor(score: number): string {
  if (score >= 90) return "bg-yellow-500";
  if (score >= 80) return "bg-green-500";
  if (score >= 70) return "bg-blue-500";
  if (score >= 60) return "bg-gray-400";
  if (score >= 50) return "bg-orange-500";
  return "bg-red-500";
}

function Medal({ rank }: { rank: number }) {
  const medals = ["🥇", "🥈", "🥉"];
  return <span className="text-base leading-none">{medals[rank - 1] || `#${rank}`}</span>;
}

function initials(name: string): string {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


/* ─────────── Encouraging labels + colors ─────────── */
function encouragingLabel(rating: string, lang: "ar" | "en"): { text: string; emoji: string } {
  const map: Record<string, { en: { text: string; emoji: string }; ar: { text: string; emoji: string } }> = {
    "Exceptional":       { en: { text: "Exceptional",       emoji: "🌟" }, ar: { text: "استثنائي",    emoji: "🌟" } },
    "Excellent":         { en: { text: "Excellent",         emoji: "⭐" }, ar: { text: "ممتاز",      emoji: "⭐" } },
    "Good":              { en: { text: "Good",              emoji: "👍" }, ar: { text: "جيد",        emoji: "👍" } },
    "Satisfactory":      { en: { text: "On Track",          emoji: "✅" }, ar: { text: "على الطريق",  emoji: "✅" } },
    "Needs Improvement": { en: { text: "Building Up",       emoji: "🌱" }, ar: { text: "في التقدم",   emoji: "🌱" } },
    "Underperforming":   { en: { text: "Room to Grow",      emoji: "🌿" }, ar: { text: "مجال للنمو",  emoji: "🌿" } },
  };
  return map[rating]?.[lang] || { text: rating, emoji: "" };
}

function encouragingBadgeColor(rating: string): string {
  if (rating === "Exceptional")       return "bg-yellow-100 text-yellow-700 border-yellow-200";
  if (rating === "Excellent")         return "bg-green-100 text-green-700 border-green-200";
  if (rating === "Good")              return "bg-blue-100 text-blue-700 border-blue-200";
  if (rating === "Satisfactory")      return "bg-teal-100 text-teal-700 border-teal-200";
  if (rating === "Needs Improvement") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-orange-100 text-orange-700 border-orange-200";
}

function scoreGradient(score: number): string {
  if (score >= 90) return "from-yellow-100 via-yellow-50 to-white";
  if (score >= 80) return "from-green-100 via-green-50 to-white";
  if (score >= 70) return "from-blue-100 via-blue-50 to-white";
  if (score >= 60) return "from-teal-100 via-teal-50 to-white";
  if (score >= 50) return "from-amber-100 via-amber-50 to-white";
  return "from-orange-100 via-orange-50 to-white";
}

function progressBarGradient(pct: number): string {
  if (pct >= 80) return "bg-gradient-to-r from-green-400 to-green-500";
  if (pct >= 60) return "bg-gradient-to-r from-blue-400 to-blue-500";
  if (pct >= 40) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
  if (pct >= 20) return "bg-gradient-to-r from-amber-400 to-amber-500";
  return "bg-gradient-to-r from-orange-400 to-orange-500";
}

function getMilestone(score: number, lang: "ar" | "en"): { needed: number; nextTier: string; emoji: string } | null {
  const tiers = [
    { min: 50, name: { en: "Building Up",  ar: "البناء" },       emoji: "🌱" },
    { min: 60, name: { en: "On Track",     ar: "على الطريق" },   emoji: "✅" },
    { min: 70, name: { en: "Good",         ar: "جيد" },          emoji: "👍" },
    { min: 80, name: { en: "Excellent",    ar: "ممتاز" },        emoji: "⭐" },
    { min: 90, name: { en: "Exceptional",  ar: "استثنائي" },    emoji: "🌟" },
  ];
  for (const t of tiers) {
    if (score < t.min) {
      return { needed: Math.ceil(t.min - score), nextTier: t.name[lang], emoji: t.emoji };
    }
  }
  return null;
}

function rankContext(rank: number, total: number, lang: "ar" | "en"): string {
  if (total <= 1) return "";
  const pct = rank / total;
  if (lang === "ar") {
    if (pct <= 0.33) return "في الثلث الأعلى 🏆";
    if (pct <= 0.66) return "في الوسط — استمر";
    return "اصعد لأعلى 💪";
  }
  if (pct <= 0.33) return "Top third 🏆";
  if (pct <= 0.66) return "Middle — keep pushing";
  return "Climb higher 💪";
}

function trendIndicator(current: number, previous: number | null, lang: "ar" | "en"): { text: string; color: string } | null {
  if (previous === null || previous === undefined) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return null;
  if (diff > 0) {
    return {
      text: lang === "ar" ? `📈 +${diff.toFixed(1)} من الشهر السابق` : `📈 +${diff.toFixed(1)} from last month`,
      color: "text-green-600",
    };
  }
  return {
    text: lang === "ar" ? `📉 ${diff.toFixed(1)} من الشهر السابق` : `📉 ${diff.toFixed(1)} from last month`,
    color: "text-orange-600",
  };
}

/* ─────────── Compact row for Top 3 entry ─────────── */
function TopRow({ tp, clickable, onClick }: { tp: any; clickable?: boolean; onClick?: () => void }) {
  const NameEl: any = clickable ? "button" : "span";
  return (
    <div className="flex items-center gap-2 py-1">
      <Medal rank={tp.rank}/>
      <NameEl
        onClick={clickable ? onClick : undefined}
        className={`flex-1 text-xs text-gray-700 truncate text-left ${clickable ? "hover:text-blue-600 hover:underline cursor-pointer" : ""}`}
      >
        {tp.name}
      </NameEl>
      <span className={`text-xs font-semibold ${scoreColor(tp.score)}`}>{tp.score.toFixed(1)}</span>
    </div>
  );
}

/* ─────────── Department mini-card ─────────── */
function DeptCard({ dept, canClick, onPick }: { dept: any; canClick?: boolean; onPick?: (id: string) => void }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-700 truncate">{dept.department}</span>
        <span className="text-[10px] text-gray-400 shrink-0 ml-1">{dept.topPerformers.length}</span>
      </div>
      <div className="space-y-0.5">
        {dept.topPerformers.map((tp: any) => (
          <TopRow
            key={tp.employeeId}
            tp={tp}
            clickable={canClick}
            onClick={() => onPick && onPick(tp.employeeId)}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────── Main Widget ─────────── */
export default function PerformanceWidget({ periodOverride, onPeriodChange }: { periodOverride?: string; onPeriodChange?: (p: string) => void } = {}) {
  const { isAdmin, isManager, isDeptHead } = useAuth();
  const navigate = useNavigate();
  const lang = useLang();
  const isRTL = lang === "ar";

  const [period, setPeriod] = useState(periodOverride || currentPeriod());
  useEffect(() => { if (periodOverride) setPeriod(periodOverride); }, [periodOverride]);
  const [data, setData]     = useState<any>(null);
  const [prevScore, setPrevScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const canClickName = isAdmin || isManager || isDeptHead;
  // Everyone now sees Top Performers by Department (previously admin-only)
  const role: string = "admin";

  useEffect(() => {
    setLoading(true);
    const endpoint =
      role === "admin"   ? `/performance/top-by-department?period=${period}` :
      role === "manager" ? `/performance/team-top?period=${period}` :
                           `/performance/my-dashboard?period=${period}`;
    api.get(endpoint)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));

    // Fetch previous month for trend (employee only)
    if (role === "employee") {
      const [py, pm] = period.split("-").map(Number);
      const prevDate = new Date(py, (pm || 1) - 2, 1);
      const prevPeriod = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, "0")}`;
      api.get(`/performance/my-dashboard?period=${prevPeriod}`)
        .then(r => setPrevScore(r.data?.finalScore ?? null))
        .catch(() => setPrevScore(null));
    }
  }, [period, role]);

  const strings = {
    titleEmp:    { en: "My Performance",               ar: "أدائي الشخصي" },
    titleMgr:    { en: "Top in My Team",               ar: "أفضل الأداء في قسمي" },
    titleAdmin:  { en: "Top Performers by Department", ar: "أفضل الأداء حسب القسم" },
    yourScore:   { en: "Score",                        ar: "الدرجة" },
    yourRank:    { en: "Rank",                         ar: "الترتيب" },
    of:          { en: "of",                           ar: "من" },
    focus:       { en: "Focus on",                     ar: "ركّز على" },
    viewFull:    { en: "View details →",               ar: "التفاصيل ←" },
  };
  const t = (s: any) => s[lang];

  const ratingLabel = (r: string) => {
    if (lang !== "ar") return r;
    const map: Record<string, string> = {
      "Exceptional": "استثنائي", "Excellent": "ممتاز", "Good": "جيد",
      "Satisfactory": "مقبول", "Needs Improvement": "يحتاج تحسين", "Underperforming": "ضعيف",
    };
    return map[r] || r;
  };

  const periodOptions = lastMonths(12);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-center h-20">
        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"/>
      </div>
    );
  }

  if (!data) return null;
  if (role === "employee" && data && data.hasDepartment === false) return null;

  const emptyManager = role === "manager" && (!data.department || (data.topPerformers || []).length === 0);
  const emptyAdmin   = role === "admin"   && (!data.departments || data.departments.length === 0);
  if (emptyManager || emptyAdmin) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-yellow-500"/>
            <span className="font-semibold text-gray-800 text-sm">{t(strings.titleAdmin)}</span>
          </div>
          <select
            value={period}
            onChange={e => { setPeriod(e.target.value); onPeriodChange?.(e.target.value); }}
            className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none"
          >
            {periodOptions.map(p => (
              <option key={p} value={p}>
                {new Date(p + "-01").toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { month: "long", year: "numeric" })}
              </option>
            ))}
          </select>
        </div>
        <div className="text-center text-gray-400 text-sm py-6">
          {lang === "ar" ? "لا توجد نقاط مسجّلة لهذا الشهر بعد" : "No points earned yet for this month"}
        </div>
      </div>
    );
  }

  const periodSelector = (
    <select
      value={period}
      onChange={e => { setPeriod(e.target.value); onPeriodChange?.(e.target.value); }}
      className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none"
    >
      {periodOptions.map(p => (
        <option key={p} value={p}>{formatPeriod(p, lang)}</option>
      ))}
    </select>
  );

  /* ═════════════ EMPLOYEE VIEW (premium & rich) ═════════════ */
  if (role === "employee") {
    const rankPct = data.deptSize > 0 ? Math.round(((data.deptSize - (data.rank - 1)) / data.deptSize) * 100) : 0;
    const encLabel = encouragingLabel(data.rating, lang);
    const milestone = getMilestone(data.finalScore, lang);
    const trend = trendIndicator(data.finalScore, prevScore, lang);
    const rankCtx = rankContext(data.rank, data.deptSize, lang);

    return (
      <div className={`bg-gradient-to-br ${scoreGradient(data.finalScore)} rounded-2xl border border-gray-100 shadow-sm p-5 ${isRTL ? "text-right" : ""}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="text-blue-600" size={18}/>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t(strings.titleEmp)}</h3>
              {data.department && <p className="text-[11px] text-gray-500">{data.department}</p>}
            </div>
          </div>
          {periodSelector}
        </div>

        {/* Score + Rank grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {/* Score card */}
          <div className="bg-white/70 backdrop-blur rounded-xl p-4 border border-white/50 shadow-sm">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">{t(strings.yourScore)}</div>
            <div className="flex items-baseline gap-1">
              <span className={`text-4xl font-bold ${scoreColor(data.finalScore)}`}>{Number(data.finalScore).toFixed(1)}</span>
              <span className="text-sm text-gray-400 font-medium">/ 100</span>
            </div>
            <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${encouragingBadgeColor(data.rating)}`}>
              <span>{encLabel.emoji}</span>
              <span>{encLabel.text}</span>
            </div>
            {trend && (
              <div className={`mt-1.5 text-[11px] font-medium ${trend.color}`}>{trend.text}</div>
            )}
          </div>

          {/* Rank card */}
          <div className="bg-white/70 backdrop-blur rounded-xl p-4 border border-white/50 shadow-sm">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">
              {lang === "ar" ? "ترتيبك" : "Your Rank"}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-gray-900">#{data.rank}</span>
              <span className="text-sm text-gray-400 font-medium">{t(strings.of)} {data.deptSize}</span>
            </div>
            <div className="w-full bg-gray-200/50 rounded-full h-1.5 mt-2.5 overflow-hidden">
              <div className={`h-full ${progressBarGradient(rankPct)} transition-all`} style={{ width: `${rankPct}%` }}/>
            </div>
            {rankCtx && <div className="mt-1.5 text-[11px] text-gray-600 font-medium">{rankCtx}</div>}
          </div>
        </div>

        {/* Next Milestone */}
        {milestone && (
          <div className="bg-white/60 backdrop-blur rounded-xl p-3 border border-white/50 mb-3 flex items-center gap-3">
            <div className="text-2xl">{milestone.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                {lang === "ar" ? "هدفك القادم" : "Next Milestone"}
              </div>
              <div className="text-sm text-gray-800 mt-0.5">
                {lang === "ar"
                  ? <>تحتاج <strong className="text-blue-600">+{milestone.needed}</strong> نقاط للوصول إلى <strong>{milestone.nextTier}</strong></>
                  : <>You need <strong className="text-blue-600">+{milestone.needed}</strong> points to reach <strong>{milestone.nextTier}</strong></>}
              </div>
            </div>
          </div>
        )}

        {/* Focus Area */}
        {data.weakest && (
          <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-white/50 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Target size={14} className="text-amber-600"/>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                {lang === "ar" ? "مجال للتحسين" : "Focus Area"}
              </span>
            </div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-gray-900">{t(data.weakest.label)}</span>
              <span className="text-xs text-gray-500 font-mono">
                {Number(data.weakest.score).toFixed(1)} / {data.weakest.max} · {data.weakest.pct}%
              </span>
            </div>
            <div className="w-full bg-gray-200/50 rounded-full h-1.5 overflow-hidden mb-2">
              <div className={`h-full ${progressBarGradient(data.weakest.pct)} transition-all`} style={{ width: `${Math.max(5, data.weakest.pct)}%` }}/>
            </div>
            <div className="flex items-start gap-1.5 text-[11px] text-gray-700 leading-relaxed">
              <span className="text-amber-500">💡</span>
              <span>{t(data.weakest.tip)}</span>
            </div>
          </div>
        )}

        <button
          onClick={() => navigate("/my-performance")}
          className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all"
        >
          {t(strings.viewFull)}
          <ChevronRight size={12}/>
        </button>
      </div>
    );
  }

  /* ═════════════ MANAGER VIEW (compact) ═════════════ */
  if (role === "manager") {
    return (
      <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 ${isRTL ? "text-right" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="text-yellow-500" size={16}/>
            <h3 className="text-sm font-semibold text-gray-900">
              {t(strings.titleMgr)}
              {data.department && <span className="text-[11px] text-gray-400 ml-1 rtl:mr-1 rtl:ml-0">· {data.department}</span>}
            </h3>
          </div>
          {periodSelector}
        </div>

        <div className="space-y-1">
          {(data.topPerformers || []).map((tp: any) => (
            <div key={tp.employeeId} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50">
              <Medal rank={tp.rank}/>
              {canClickName ? (
                <button
                  onClick={() => navigate(`/my-performance?user=${tp.employeeId}`)}
                  className="flex-1 text-sm text-gray-800 truncate text-left hover:text-blue-600 hover:underline cursor-pointer"
                >
                  {tp.name}
                </button>
              ) : (
                <span className="flex-1 text-sm text-gray-800 truncate">{tp.name}</span>
              )}
              <div className="w-20 bg-gray-100 rounded-full h-1.5 overflow-hidden shrink-0">
                <div className={`h-full ${scoreBarColor(tp.score)}`} style={{ width: `${tp.score}%` }}/>
              </div>
              <span className={`text-sm font-semibold ${scoreColor(tp.score)} min-w-[32px] text-right`}>{tp.score.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ═════════════ ADMIN VIEW (compact grid) ═════════════ */
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 ${isRTL ? "text-right" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="text-yellow-500" size={16}/>
          <h3 className="text-sm font-semibold text-gray-900">{t(strings.titleAdmin)}</h3>
        </div>
        {periodSelector}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {data.departments.map((dept: any) => (
          <DeptCard
            key={dept.department}
            dept={dept}
            canClick={canClickName}
            onPick={(id) => navigate(`/my-performance?user=${id}`)}
          />
        ))}
      </div>
    </div>
  );
}
